import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useId } from 'react';
import { cn } from '@/lib/cn';

const CONTROL =
  'w-full rounded-control border border-border bg-surface-raised px-3.5 py-2.5 text-[13px] text-content transition focus:border-primary/50';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Render the label for screen readers only. */
  hideLabel?: boolean;
}

export function TextField({ label, hideLabel = false, className, ...props }: TextFieldProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'font-mono text-[10px] uppercase tracking-[0.12em] text-content-muted',
          hideLabel && 'sr-only',
        )}
      >
        {label}
      </label>
      <input id={id} className={cn(CONTROL, className)} {...props} />
    </div>
  );
}

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hideLabel?: boolean;
}

export function TextArea({ label, hideLabel = false, className, ...props }: TextAreaProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'font-mono text-[10px] uppercase tracking-[0.12em] text-content-muted',
          hideLabel && 'sr-only',
        )}
      >
        {label}
      </label>
      <textarea id={id} className={cn(CONTROL, 'min-h-24 resize-y', className)} {...props} />
    </div>
  );
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  hideLabel?: boolean;
  options: readonly { value: string; label: string }[];
}

export function SelectField({
  label,
  hideLabel = false,
  options,
  className,
  ...props
}: SelectFieldProps) {
  const id = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn(
          'font-mono text-[10px] uppercase tracking-[0.12em] text-content-muted',
          hideLabel && 'sr-only',
        )}
      >
        {label}
      </label>
      <select id={id} className={cn(CONTROL, 'appearance-none', className)} {...props}>
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-surface-raised">
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
